import React from "react";
import { colors } from "utils";

const PRESENT_COLOR = "#1F7A4D";
const ABSENT_COLOR = "#B23A2E";

/**
 * A row is shown as "partial" when the backend reports it absent, names the one
 * unpersisted input inside it, and its sibling inputs are stored. "partial" is a
 * display verdict derived here — the wire contract carries a strict boolean
 * `present` and never a third value.
 */
export const verdictFor = (section) => {
  if (section.present) return "present";
  if (section.not_persisted_part) return "partial";
  return "absent";
};

const verdictColor = (verdict) => (verdict === "present" ? PRESENT_COLOR : ABSENT_COLOR);

const cellStyle = {
  padding: "7px 14px",
  borderTop: "1px solid #eee8e0",
  verticalAlign: "top"
};

const noteStyle = {
  color: colors.nightlyWoods500,
  fontSize: "12px"
};

const reasonCodeStyle = {
  backgroundColor: "#f1ede7",
  borderRadius: "3px",
  color: "#536F7A",
  fontSize: "12px",
  padding: "1px 5px"
};

const markStyle = {
  display: "inline-block",
  height: "7px",
  marginRight: "5px",
  verticalAlign: "middle",
  width: "7px"
};

/**
 * A reader scanning eleven rows will not read three sentences to find out which
 * collaboration rows came from the same place, so each note carries a mark. The
 * mark is chosen by the note's VALUE — the nth distinct note in this manifest —
 * and never by its wording, which is a backend constant and still moving. It
 * says "these two rows came from the same place and that one did not", which is
 * the question being asked at a glance; the sentence beside it answers the rest.
 */
const PROVENANCE_MARKS = [
  { ...markStyle, backgroundColor: colors.nightlyWoods500, borderRadius: "50%" },
  { ...markStyle, border: `1px solid ${colors.nightlyWoods500}`, borderRadius: "50%" },
  { ...markStyle, backgroundColor: colors.nightlyWoods500 }
];

const provenanceMarkFor = (rank) => PROVENANCE_MARKS[rank % PROVENANCE_MARKS.length];

const distinctProvenanceNotes = (sections) => [
  ...new Set(sections.map((section) => section.derived_note).filter(Boolean))
];

const ManifestRow = ({ section, index, provenanceRank }) => {
  const verdict = verdictFor(section);

  return (
    <tr>
      <td style={{ ...cellStyle, width: "26px", color: colors.nightlyWoods500 }}>{index + 1}</td>
      <td style={cellStyle}>
        {section.name}
        {section.shared_value_note && (
          <div style={noteStyle}>{section.shared_value_note}</div>
        )}
        {section.derived_note && (
          <div style={noteStyle}>
            <span aria-hidden="true" style={provenanceMarkFor(provenanceRank)} />
            {section.derived_note}
          </div>
        )}
      </td>
      <td style={{ ...cellStyle, width: "82px", fontWeight: "bold", color: verdictColor(verdict) }}>
        {verdict}
      </td>
      <td style={{ ...cellStyle, width: "200px" }}>
        {section.reason && <span style={reasonCodeStyle}>{section.reason}</span>}
        {section.not_persisted_part && (
          <div style={noteStyle}>{section.not_persisted_part}</div>
        )}
      </td>
    </tr>
  );
};

export const ReportInputsManifest = ({ sections }) => {
  if (!sections) return null;

  const attached = sections.filter((section) => section.present).length;
  const provenanceNotes = distinctProvenanceNotes(sections);

  return (
    <div style={{
      border: "1px solid #d9d2c8",
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: "20px"
    }}>
      <div style={{
        alignItems: "center",
        backgroundColor: "#F4F4FD",
        display: "flex",
        fontSize: "13px",
        fontWeight: "bold",
        justifyContent: "space-between",
        padding: "10px 14px"
      }}>
        <span>Report Inputs</span>
        <span style={{ color: colors.nightlyWoods500, fontSize: "12px", fontWeight: "normal" }}>
          {`${attached} of ${sections.length} attached`}
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
        <tbody>
          {sections.map((section, index) => (
            <ManifestRow
              key={section.name}
              section={section}
              index={index}
              provenanceRank={provenanceNotes.indexOf(section.derived_note)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
